/*
 * Copyright (c) 2018 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can';
import Component from 'can/component/component';
import template from './history-player-statistics.stache';
import appContext from 'scripts/app-context';

const playerPageUrl = window.globalSettings.pagePaths.player;

const scoringLeaderboardUrl = window.globalSettings.feeds.history.scoring.stats;
const subscriptionKey = window.globalSettings.feeds.history.subscriptionKey;

const MAP_FEEDS = {
    'driving-distance': 'drive',
    'fairways-hit': 'fairwayhit',
    'greens-in-regulation': 'gir',
    putting: 'putting',
    birdies: 'birdies',
    eagles: 'eagles',
};

const buildUrl = function (year, point) {
    return can.sub(scoringLeaderboardUrl, {year, point});
};

const mapSortFields = {
    rank: 'rank',
    pos: 'pos',
    player: 'lastName',
};

const lettersRegexp = /[^\d]+/g;

const simpleKpis = ['driving-distance', 'putting', 'birdies', 'eagles'];

Component.extend({
    tag: 'history-player-statistics',
    template: template,
    leakScope: false,
    viewModel: {
        define: {
            fetching: {
                type: 'boolean',
                value: true,
            },
            kpi: {
                type: 'string',
                value: 'fairways-hit',
            },
            isKpiSimple: {
                type: 'boolean',
                get() {
                    return Boolean(~simpleKpis.indexOf(this.attr('kpi')));
                },
            },
            settings: {
                type: 'object',
            },
            sortKey: {
                type: 'string',
                value: '',
            },
            sortAsc: {
                type: 'boolean',
                value: true,
            },
            players: {
                Type: can.List,
                Value: can.List,
            },
            yearSelected: {
                type: 'number',
                value: 2020, // todo make this automated
            },
            dataNotAvailable: {
                type: 'boolean',
                value: false,
            },
        },

        playerDetailLink(playerId) {
            return can.sub(playerPageUrl, {playerId});
        },

        getLegend(currentYear) {
            const years = this.attr('settings.legendYears');
            if (
                years &&
                currentYear &&
                years.indexOf(currentYear.toString()) != -1
            ) {
                return this.attr('settings.legendYearText');
            }
            return false;
        },
    },

    events: {
        inserted() {
            appContext.model.bind(
                'history-experience-scoring-and-stats-years-change',
                this.handleYearChange.bind(this),
            );
            appContext.model.bind(
                'history-experience-stats-kpi-change',
                this.handleKpiChange.bind(this),
            );

            appContext.model.dispatch('history-experience-stats-ready');

            this.fetchYears();
        },

        fetchYears() {
            console.log('HERE fetchYears', {
                type: 'GET',
                url: buildUrl('meta', MAP_FEEDS[this.viewModel.attr('kpi')]),
                headers: subscriptionKey
                    ? {['Ocp-Apim-Subscription-Key']: subscriptionKey}
                    : {}
            });

            can.ajax({
                    type: 'GET',
                    url: buildUrl('meta', MAP_FEEDS[this.viewModel.attr('kpi')]),
                    headers: subscriptionKey
                        ? {['Ocp-Apim-Subscription-Key']: subscriptionKey}
                        : {}
                })
                .done((data) => {
                    appContext.model.dispatch(
                        'history-experience-scoring-and-stats-years-ready',
                        [data.years],
                    );
                })
                .fail((response, status, error) => {
                    console.log('Cannot fetch years...', error);
                });
        },

        fetchData() {
            if (
                !this.viewModel.attr('yearSelected') ||
                !this.viewModel.attr('kpi')
            ) {
                return false;
            }
            this.viewModel.attr('fetching', true);
            const url = buildUrl(
                this.viewModel.attr('yearSelected'),
                MAP_FEEDS[this.viewModel.attr('kpi')],
            );

            console.log('HERE fetchData', {
                type: 'GET',
                url: url,
                headers: subscriptionKey
                    ? {['Ocp-Apim-Subscription-Key']: subscriptionKey}
                    : {}
            });

            can.ajax({
                    type: 'GET',
                    url: url,
                    headers: subscriptionKey
                        ? {['Ocp-Apim-Subscription-Key']: subscriptionKey}
                        : {}
                })
                .done((data) => {
                    this.viewModel.attr('fetching', false);
                    this.viewModel.attr('dataNotAvailable', false);
                    this.viewModel.attr('players', data.players);
                    this.viewModel
                        .attr('players')
                        .attr('comparator', function (a, b) {
                            a = a.attr('rank')
                                ? +a
                                    .attr('rank')
                                    .toString()
                                    .replace(lettersRegexp, '')
                                : '-';
                            b = b.attr('rank')
                                ? +b
                                    .attr('rank')
                                    .toString()
                                    .replace(lettersRegexp, '')
                                : '-';
                            if (a === '-') {
                                return 1;
                            }
                            if (b === '-') {
                                return -1;
                            }

                            let res = a === b ? 0 : a > b ? 1 : -1;
                            return res;
                        });
                })
                .fail((response, status, error) => {
                    console.log(`Error - ${error}`);
                    this.viewModel.attr('dataNotAvailable', true);
                    this.viewModel.attr('fetching', false);
                });
        },

        '.js-sorting-field click'(element, event) {
            return false;
            /*const sortKey = element.attr('data-sort-key');
            can.batch.start();
            if (this.viewModel.attr('sortKey') === sortKey) {
                this.viewModel.attr('sortAsc', !this.viewModel.attr('sortAsc'));
            } else {
                this.viewModel.attr('sortKey', sortKey);
                this.viewModel.attr('sortAsc', true);
            }
            this.updateComparator();
            can.batch.stop();*/
        },

        updateComparator() {
            const players = this.viewModel.attr('players');
            if (players) {
                players.attr(
                    'comparator',
                    this.getSortingFunc(
                        this.viewModel.attr('sortKey'),
                        this.viewModel.attr('sortAsc'),
                    ),
                );
            }
        },

        getSortingFunc(sortKey, sortAsc) {
            const field = mapSortFields[sortKey];
            return (a, b) => {
                a = a.attr(field) || '-';
                b = b.attr(field) || '-';

                if (a === '-') {
                    return 1;
                }
                if (b === '-') {
                    return -1;
                }

                let res = a === b ? 0 : a > b ? 1 : -1;
                return sortAsc ? res : res * -1;
            };
        },

        handleYearChange(event, year) {
            this.viewModel.attr('yearSelected', year);
            this.fetchData();
        },

        handleKpiChange(event, kpi) {
            this.viewModel.attr('kpi', kpi);
            this.fetchYears();
            this.fetchData();
        },
    },
});
